import axios from 'axios';
import { fromJS, Map, Set } from 'immutable';
import { dispatch, registerHandler } from 'nike-re-framejs';
import { catcher } from '@/_http/utils';
import { deepMerge } from 'nike-ui/src/common/immutable/merge';
import { getUrl } from '@/common/oldMobile';
import { defaultMiddlewares } from '@/common/util';
import { saveFavoritesMarketsToLS, saveFavoritesMenuItemsToLS } from '@/favorites/api';
import { getKey, setKey } from '@/services/localStorage';
import { close } from '@/services/push';
import PopupNg from '@/ui/PopupNg';
import throttle from 'lodash/throttle';
import { storeLastLoggedInCustomerId } from 'nike-ui/src/common/logging/utils';
import { loadNewAccountMessages } from '@/message/handlers';

registerHandler(
    'account.updateUserData',
    defaultMiddlewares,
    throttle(db => {
        axios
            .get('/api/account/data')
            .then(data => {
                if (db.get('loggedIn') && !data.cisloZmluvy) {
                    dispatch(['account.onSessionExpired']);
                    return;
                }
                dispatch(['account.userDataUpdated', data]);
                dispatch(loadUserSettings);
                dispatch(loadNewAccountMessages);
            })
            .catch(error => {
                if (error && error.response && error.response.status === 401) {
                    window.location.replace('/doLogout');
                }
            });

        return db;
    }, 1000)
);

export function userDataUpdated(data) {
    const avatar = getKey('nikeUserAvatar');

    if (data.cisloZmluvy) {
        storeLastLoggedInCustomerId(data.cisloZmluvy);
    }

    if (!data.cisloZmluvy) {
        return db =>
            db
                .removeIn(['account', 'session'])
                .removeIn(['account', 'userData'])
                .setIn(['account', 'userData'], Map({ avatar }));
    }

    const userData = fromJS(data);
    return db =>
        db
            .updateIn(['account', 'userData'], prev => deepMerge(prev, userData))
            .updateIn(['account', 'userData', 'avatar'], prev => deepMerge(prev, avatar));
}

export const loadUserSettings = db => {
    if (db.get('loggedIn')) {
        axios
            .get('/api/account/load-user-settings')
            .then(usersettings => {
                dispatch(saveUserSettings(usersettings));
            })
            .catch(catcher.swallow);
    }
};

const saveUserSettings = usersettings => db => {
    const avatar = usersettings.avatar || getKey('nikeUserAvatar');
    saveAvatarToLocalStorage(avatar);

    return db
        .updateIn(['account', 'userData', 'avatar'], prev => deepMerge(prev, usersettings.avatar))
        .update(userFavoritesUpdated(usersettings.favourite));
};

export function userFavoritesUpdated(favourite) {
    const menuItems = Set(favourite.menuItems);
    const markets = Set(favourite.markets);
    const casinoVegasGames = fromJS(favourite.casinoVegasGames);
    const sportEvents = fromJS(favourite.sportEvents);
    const tlvMarkets = fromJS(favourite.tlvMarkets);
    const tlvMarketsGlobal = Set(favourite.tlvMarketsGlobal);
    const tlvOverviewMarkets = fromJS(favourite.tlvOverviewMarkets);
    const tournaments = fromJS(favourite.tournaments);
    saveFavoritesMenuItemsToLS(menuItems);
    saveFavoritesMarketsToLS(markets);

    return db =>
        db
            .updateIn(['account', 'userFavourites', 'menuItems'], prev => deepMerge(prev, menuItems))
            .updateIn(['account', 'userFavourites', 'markets'], prev => deepMerge(prev, markets))
            .updateIn(['account', 'userFavourites', 'casinoVegasGames'], prev => deepMerge(prev, casinoVegasGames))
            .updateIn(['account', 'userFavourites', 'turboLive', 'matches'], prev => deepMerge(prev, sportEvents))
            .updateIn(['account', 'userFavourites', 'turboLive', 'tournaments'], prev => deepMerge(prev, tournaments))
            .updateIn(['account', 'userFavourites', 'turboLive', 'markets'], prev => deepMerge(prev, tlvMarkets))
            .updateIn(['account', 'userFavourites', 'turboLive', 'marketsGlobal'], prev =>
                deepMerge(prev, tlvMarketsGlobal)
            )
            .updateIn(['account', 'userFavourites', 'turboLive', 'overviewMarkets'], prev =>
                deepMerge(prev, tlvOverviewMarkets)
            );
}

function saveAvatarToLocalStorage(avatarId) {
    if (avatarId) {
        setKey('nikeUserAvatar', avatarId);
    }
}

registerHandler('account.userDataUpdated', defaultMiddlewares, (db, [data]) => db.update(userDataUpdated(data)));

registerHandler('account.onSessionExpired', defaultMiddlewares, db =>
    db
        .setIn(['loggedIn'], false)
        .update(userDataUpdated({}))
        .update(
            PopupNg.updater.show(
                {
                    style: 'warn',
                    title: 'START_LOGOUT',
                    messages: 'jsp.dialog.sessionTimeoutErrorMessage',
                    close: ['ui.reloadHome']
                },
                1001
            )
        )
        .update(close)
);

registerHandler('account.forcedPasswordChanged', defaultMiddlewares, db =>
    db.setIn(['account', 'forcedPasswordChanged', true])
);

registerHandler('account.userData.avatar', defaultMiddlewares, (db, [avatarId]) => {
    axios
        .put(getUrl('AVATAR'), avatarId, { headers: { 'Content-Type': 'application/json' } })
        .catch(catcher.swallow)
        .then(() => saveAvatarToLocalStorage(avatarId));
    return db.setIn(['account', 'userData', 'avatar'], avatarId);
});
registerHandler('commit-update', defaultMiddlewares, (db, [updater]) => {
    return db.update(updater);
});

export const setForcedActionsCrm = forcedActionsCrm => db =>
    db.updateIn(['account', 'userData', 'forcedActionsCrm'], existing => deepMerge(existing, forcedActionsCrm));
